import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import XYZ from "ol/source/XYZ";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Polygon from "ol/geom/Polygon";
import LineString from "ol/geom/LineString";
import Point from "ol/geom/Point";
import { fromLonLat, toLonLat } from "ol/proj";
import { defaults as defaultControls } from "ol/control";
import { Style, Stroke, Fill } from "ol/style";
import axios from "axios";
import tiandituConfig from "../config/tianditu";
import { bd09ToWgs84, wgs84ToBd09 } from "./coordTransform";

import "ol/ol.css";

const { tiandituKey } = tiandituConfig;

function createTiandituLayer(type) {
  return new TileLayer({
    source: new XYZ({
      crossOrigin: "anonymous",
      url: `https://t{0-7}.tianditu.gov.cn/DataServer?T=${type}_w&x={x}&y={y}&l={z}&tk=${tiandituKey}`,
    }),
  });
}

export function bd09ToMapCoord(lng, lat) {
  const wgs = bd09ToWgs84(Number(lng), Number(lat));
  return fromLonLat(wgs);
}

export function mapCoordToBd09(coordinate) {
  const [lng, lat] = toLonLat(coordinate);
  return wgs84ToBd09(lng, lat);
}

export function parseBoundaryString(boundaryStr) {
  return boundaryStr.split(";").map((pair) => {
    const parts = pair.split(",");
    return [parseFloat(parts[0].trim()), parseFloat(parts[1].trim())];
  });
}

export function createTiandituMap(target, options = {}) {
  const {
    center = [118.503916, 37.453142],
    zoom = 10,
    dark = false,
    minZoom = 3,
    maxZoom = 18,
  } = options;

  const baseType = dark ? "img" : "vec";
  const baseLayer = createTiandituLayer(baseType);
  const labelLayer = createTiandituLayer("cva");
  const vectorSource = new VectorSource();
  const vectorLayer = new VectorLayer({
    source: vectorSource,
    zIndex: 10,
  });

  const map = new Map({
    target,
    layers: [baseLayer, labelLayer, vectorLayer],
    view: new View({
      center: bd09ToMapCoord(center[0], center[1]),
      zoom,
      minZoom,
      maxZoom,
    }),
    controls: defaultControls({ rotate: false }),
  });

  map.__vectorSource = vectorSource;
  map.__vectorLayer = vectorLayer;
  map.__overlays = [];

  return map;
}

export function destroyTiandituMap(map) {
  if (!map) return;
  map.getOverlays().clear();
  map.setTarget(null);
}

export function setMapCenterByBd09(map, lng, lat, zoom) {
  const view = map.getView();
  view.setCenter(bd09ToMapCoord(lng, lat));
  if (zoom !== undefined) {
    view.setZoom(zoom);
  }
}

export function fitMapToBd09Coords(map, coords, options = {}) {
  if (!coords || !coords.length) return;
  const extent = coords.reduce(
    (acc, item) => {
      const coord = bd09ToMapCoord(item[0], item[1]);
      return [
        Math.min(acc[0], coord[0]),
        Math.min(acc[1], coord[1]),
        Math.max(acc[2], coord[0]),
        Math.max(acc[3], coord[1]),
      ];
    },
    [Infinity, Infinity, -Infinity, -Infinity]
  );
  map.getView().fit(extent, {
    padding: options.padding || [40, 40, 40, 40],
    maxZoom: options.maxZoom || 16,
    duration: options.duration || 0,
  });
}

export function clearMapVectorFeatures(map) {
  map.__vectorSource.clear();
}

export function addBoundaryPolygon(map, boundaryStr, styleOptions = {}) {
  const ring = parseBoundaryString(boundaryStr).map((item) =>
    bd09ToMapCoord(item[0], item[1])
  );
  if (ring.length > 0) {
    ring.push(ring[0]);
  }
  const feature = new Feature({
    geometry: new Polygon([ring]),
  });
  feature.setStyle(
    new Style({
      stroke: new Stroke({
        color: styleOptions.strokeColor || "#4e6481",
        width: styleOptions.strokeWeight || 2,
        lineDash: styleOptions.strokeStyle === "dashed" ? [8, 8] : undefined,
      }),
      fill: new Fill({
        color: styleOptions.fillColor || "rgba(120,159,169,0.5)",
      }),
    })
  );
  map.__vectorSource.addFeature(feature);
  return feature;
}

export function addPolylineByBd09(map, coords, styleOptions = {}) {
  const lineCoords = coords.map((item) => bd09ToMapCoord(item[0], item[1]));
  const feature = new Feature({
    geometry: new LineString(lineCoords),
  });
  feature.setStyle(
    new Style({
      stroke: new Stroke({
        color: styleOptions.strokeColor || "rgba(0,0,0,0.7)",
        width: styleOptions.strokeWeight || 4,
      }),
    })
  );
  map.__vectorSource.addFeature(feature);
  return feature;
}

export function addPolygonByBd09(map, coords, styleOptions = {}) {
  const ring = coords.map((item) => bd09ToMapCoord(item.lng || item[0], item.lat || item[1]));
  if (ring.length > 0) {
    ring.push(ring[0]);
  }
  const feature = new Feature({
    geometry: new Polygon([ring]),
  });
  feature.setStyle(
    new Style({
      stroke: new Stroke({
        color: styleOptions.strokeColor || "#409EFF",
        width: styleOptions.strokeWeight || 2,
      }),
      fill: new Fill({
        color: styleOptions.fillColor || "rgba(64,158,255,0.3)",
      }),
    })
  );
  map.__vectorSource.addFeature(feature);
  return feature;
}

export function bindMapClickToBd09(map, handler) {
  map.on("singleclick", (evt) => {
    const bd09 = mapCoordToBd09(evt.coordinate);
    handler({
      lng: bd09[0],
      lat: bd09[1],
      coordinate: evt.coordinate,
      pixel: evt.pixel,
    });
  });
}

export async function reverseGeocodeByBd09(lng, lat) {
  const wgs = bd09ToWgs84(Number(lng), Number(lat));
  const postStr = JSON.stringify({ lon: wgs[0], lat: wgs[1], ver: 1 });
  try {
    const res = await axios.get("https://api.tianditu.gov.cn/geocoder", {
      params: {
        postStr,
        type: "geocode",
        tk: tiandituKey,
      },
    });
    const data = res.data || {};
    if (data.status === "0" && data.result) {
      return data.result.formatted_address || data.result.addressComponent?.address || "";
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
  return `${lng},${lat}`;
}

export function createPathPointFeature(map, lng, lat) {
  return new Feature({
    geometry: new Point(bd09ToMapCoord(lng, lat)),
  });
}

export function getBd09CoordsFromExtent(extentCoords) {
  return extentCoords.map((coord) => {
    const bd09 = mapCoordToBd09(coord);
    return { lng: bd09[0], lat: bd09[1] };
  });
}

export function getRectangleCoordsFromGeometry(geometry) {
  const extent = geometry.getExtent();
  const sw = mapCoordToBd09([extent[0], extent[1]]);
  const ne = mapCoordToBd09([extent[2], extent[3]]);
  return [
    { lng: sw[0], lat: sw[1] },
    { lng: ne[0], lat: sw[1] },
    { lng: ne[0], lat: ne[1] },
    { lng: sw[0], lat: ne[1] },
  ];
}
